import clsx from "clsx";
import { useCallback, useState } from "react";
import { REAL_PLAYERS_INFO } from "../../../../common/constants.ts";
import type { View } from "../../../../common/types.ts";
import { MobileContentHero } from "../mobile/mobilePrimitives.tsx";
import { MobileTooltip } from "../components/MobileTooltip.tsx";
import { TeamLogoInline } from "../components/TeamLogoInline.tsx";
import useTitleBar from "../hooks/useTitleBar.tsx";
import { confirm } from "../util/confirm.tsx";
import { decisionConfirmVariants } from "../util/decisionConfirmVariants.ts";
import i18n from "../util/i18n.ts";
import { translatePhaseText } from "../util/phaseText.ts";
import { translateTeamName, translateTeamRegion } from "../util/teamNames.ts";
import { toWorker } from "../util/toWorker.ts";
import { getCareerTenure, getDashboardLeagueState } from "./dashboardState.ts";

type League = View<"dashboard">["leagues"][number];

const formatRelativeTime = (date: Date) => {
  const diffSeconds = Math.round((date.getTime() - Date.now()) / 1000);
  const absDiffSeconds = Math.abs(diffSeconds);
  const locale = i18n.language === "zh" ? "zh-CN" : "en";

  if (absDiffSeconds < 45) {
    return i18n.t("dashboard.justNow", {
      ns: "ui",
      defaultValue: "just now",
    });
  }

  const rtf = new Intl.RelativeTimeFormat(locale, {
    numeric: "auto",
  });

  const units = [
    { unit: "minute" as const, seconds: 60, limit: 45 * 60 },
    { unit: "hour" as const, seconds: 60 * 60, limit: 22 * 60 * 60 },
    { unit: "day" as const, seconds: 24 * 60 * 60, limit: 26 * 24 * 60 * 60 },
    {
      unit: "month" as const,
      seconds: Math.round(30.4375 * 24 * 60 * 60),
      limit: Math.round(320 * 24 * 60 * 60),
    },
    {
      unit: "year" as const,
      seconds: Math.round(365.25 * 24 * 60 * 60),
      limit: Number.POSITIVE_INFINITY,
    },
  ];

  for (const { limit, seconds, unit } of units) {
    if (absDiffSeconds < limit) {
      return rtf.format(Math.round(diffSeconds / seconds), unit);
    }
  }

  return rtf.format(Math.round(diffSeconds / (365.25 * 24 * 60 * 60)), "year");
};

const ContinueButton = ({
  disabled,
  lid,
  onClick,
  throbbing,
}: {
  disabled: boolean;
  lid: number;
  onClick: () => void;
  throbbing: boolean;
}) => {
  const continueLabel = i18n.t("dashboard.continueLeague", {
    ns: "ui",
    defaultValue: "Continue career",
  });

  if (!disabled && !throbbing) {
    return (
      <a className="btn btn-primary w-100" href={`/l/${lid}`} onClick={onClick}>
        {continueLabel}
      </a>
    );
  }

  return (
    <button className="btn btn-primary w-100" disabled type="button">
      {throbbing ? (
        <span aria-hidden="true" className="spinner-border spinner-border-sm me-2" />
      ) : null}
      {continueLabel}
    </button>
  );
};

const LeagueActionMenu = ({
  disabled,
  league,
  setDeletingLID,
}: {
  disabled: boolean;
  league: League;
  setDeletingLID: (lid: number | undefined) => void;
}) => {
  const [actuallyStarred, setActuallyStarred] = useState<boolean>(!!league.starred);
  const actionsLabel = i18n.t("dashboard.actions", {
    ns: "ui",
    defaultValue: "League actions",
  });
  const starLabel = i18n.t(actuallyStarred ? "dashboard.unstar" : "dashboard.star", {
    ns: "ui",
    defaultValue: actuallyStarred ? "Unstar" : "Star",
  });

  const toggleStar = useCallback(async () => {
    const newStarred = !actuallyStarred;
    setActuallyStarred(newStarred);
    await toWorker("main", "updateLeague", {
      lid: league.lid,
      obj: {
        starred: newStarred,
      },
    });
  }, [actuallyStarred, league.lid]);

  return (
    <details
      className="position-relative"
      data-dashboard-league-menu=""
      data-dashboard-league-menu-lid={league.lid}
      data-native-details-host="dashboard-league-menu"
    >
      <MobileTooltip title={actionsLabel}>
        <summary
          aria-label={actionsLabel}
          className={clsx(
            "btn btn-link border-0 p-1 text-body-secondary",
            disabled ? "disabled" : undefined,
          )}
          id={`dashboard-actions-${league.lid}`}
          role="button"
        >
          <span aria-hidden="true">...</span>
        </summary>
      </MobileTooltip>
      <div
        className="position-absolute end-0 mt-1 d-grid gap-1 rounded border bg-body p-2 shadow-sm"
        style={{ minWidth: 160, zIndex: 2 }}
      >
        <button
          className="btn btn-sm btn-link text-start text-body"
          disabled={disabled}
          onClick={() => {
            void toggleStar();
          }}
          type="button"
        >
          {starLabel}
        </button>
        <button
          className="btn btn-sm btn-link text-start text-body"
          disabled={disabled}
          onClick={async () => {
            const newName = await confirm(
              i18n.t("dashboard.leagueName", {
                ns: "ui",
                defaultValue: "League name:",
              }),
              {
                defaultValue: league.name,
                okText: i18n.t("dashboard.renameLeague", {
                  ns: "ui",
                  defaultValue: "Rename league",
                }),
              },
            );

            if (typeof newName === "string" && newName.trim() !== "") {
              await toWorker("main", "updateLeague", {
                lid: league.lid,
                obj: {
                  name: newName,
                },
              });
            }
          }}
        >
          {i18n.t("dashboard.rename", { ns: "ui", defaultValue: "Rename" })}
        </button>
        <button
          className="btn btn-sm btn-link text-start text-danger"
          data-dashboard-delete-league-lid={league.lid}
          disabled={disabled}
          onClick={async (event) => {
            const deleteButton = event.currentTarget;
            const proceed = await confirm(
              i18n.t("dashboard.deleteConfirm", {
                ns: "ui",
                defaultValue:
                  'Are you absolutely sure you want to delete "{{name}}"? You will permanently lose any record of all seasons, players, and games from this league.',
                name: league.name,
              }),
              {
                okText: i18n.t("dashboard.deleteLeague", {
                  ns: "ui",
                  defaultValue: "Delete League",
                }),
                okButtonVariant: "danger",
                variant: decisionConfirmVariants.dashboardDeleteLeague,
              },
            );

            if (proceed) {
              setDeletingLID(league.lid);
              try {
                await toWorker("main", "removeLeague", league.lid);
              } finally {
                setDeletingLID(undefined);
              }
            } else {
              deleteButton.focus();
            }
          }}
          type="button"
        >
          {i18n.t("dashboard.delete", { ns: "ui", defaultValue: "Delete" })}
        </button>
      </div>
    </details>
  );
};

const LeagueCard = ({
  disabled,
  league,
  setDeletingLID,
  setLoadingLID,
  throbbing,
}: {
  disabled: boolean;
  league: League;
  setDeletingLID: (lid: number | undefined) => void;
  setLoadingLID: (lid: number | undefined) => void;
  throbbing: boolean;
}) => {
  const translatedTeamRegion = translateTeamRegion(league.teamRegion);
  const translatedTeamName = translateTeamName(league.teamName);
  const teamName = `${translatedTeamRegion} ${translatedTeamName}`;
  const careerTenure = getCareerTenure(league);
  const lastPlayed = league.lastPlayed
    ? formatRelativeTime(league.lastPlayed)
    : i18n.t("dashboard.notPlayedYet", {
        ns: "ui",
        defaultValue: "Not played yet",
      });

  return (
    <article className="card h-100" data-dashboard-league-card-lid={league.lid}>
      <div className="card-body d-flex flex-column gap-3">
        <div className="d-flex align-items-start justify-content-between gap-2">
          <div className="overflow-hidden">
            <h2 className="h5 mb-2 text-truncate">
              {disabled ? (
                <span>{league.name}</span>
              ) : (
                <a
                  className="link-body-emphasis text-decoration-none"
                  href={`/l/${league.lid}`}
                  onClick={() => setLoadingLID(league.lid)}
                >
                  {league.name}
                </a>
              )}
            </h2>
            <div className="d-flex align-items-center text-body-secondary">
              <TeamLogoInline imgURL={league.imgURL} size={32} className="me-2" />
              <span className="text-truncate">{teamName}</span>
            </div>
            {careerTenure !== undefined ? (
              <div className="small text-body-secondary mt-1" data-dashboard-career-tenure="">
                {i18n.t("dashboard.careerTenure", {
                  ns: "ui",
                  count: careerTenure,
                  defaultValue: "Season {{count}} as GM",
                })}
              </div>
            ) : null}
          </div>
          <LeagueActionMenu disabled={disabled} league={league} setDeletingLID={setDeletingLID} />
        </div>

        <div className="row g-3">
          <div className="col-6">
            <div className="small text-body-secondary">
              {i18n.t("dashboard.phase", { ns: "ui", defaultValue: "Phase" })}
            </div>
            <div className="fw-semibold">{translatePhaseText(league.phaseText)}</div>
          </div>
          <div className="col-6">
            <div className="small text-body-secondary">
              {i18n.t("dashboard.lastPlayed", {
                ns: "ui",
                defaultValue: "Last played",
              })}
            </div>
            <MobileTooltip title={league.lastPlayed?.toLocaleString() ?? lastPlayed}>
              <div className="fw-semibold">{lastPlayed}</div>
            </MobileTooltip>
          </div>
        </div>

        <div className="mt-auto">
          {!disabled && !throbbing ? (
            <a
              className="btn btn-outline-secondary btn-sm w-100"
              data-dashboard-secondary-enter
              href={`/l/${league.lid}`}
              onClick={() => setLoadingLID(league.lid)}
            >
              {i18n.t("dashboard.openLeague", {
                ns: "ui",
                defaultValue: "Open league",
              })}
            </a>
          ) : (
            <button className="btn btn-outline-secondary btn-sm w-100" disabled type="button">
              {throbbing ? (
                <span aria-hidden="true" className="spinner-border spinner-border-sm me-2" />
              ) : null}
              {i18n.t("dashboard.openLeague", {
                ns: "ui",
                defaultValue: "Open league",
              })}
            </button>
          )}
        </div>
      </div>
    </article>
  );
};

const LeagueGrid = ({
  disabled,
  leagues,
  setDeletingLID,
  setLoadingLID,
  loadingLID,
}: {
  disabled: boolean;
  leagues: League[];
  setDeletingLID: (lid: number | undefined) => void;
  setLoadingLID: (lid: number | undefined) => void;
  loadingLID: number | undefined;
}) => (
  <div className="row row-cols-1 row-cols-md-2 row-cols-xl-3 g-3">
    {leagues.map((league) => (
      <div className="col" key={league.lid}>
        <LeagueCard
          disabled={disabled}
          league={league}
          setDeletingLID={setDeletingLID}
          setLoadingLID={setLoadingLID}
          throbbing={loadingLID === league.lid}
        />
      </div>
    ))}
  </div>
);

const MobileSaveHero = ({
  disabled,
  league,
  setLoadingLID,
  throbbing,
}: {
  disabled: boolean;
  league: League;
  setLoadingLID: (lid: number | undefined) => void;
  throbbing: boolean;
}) => {
  const translatedTeamRegion = translateTeamRegion(league.teamRegion);
  const translatedTeamName = translateTeamName(league.teamName);
  const teamName = `${translatedTeamRegion} ${translatedTeamName}`;
  const careerTenure = getCareerTenure(league);
  const lastPlayed = league.lastPlayed
    ? formatRelativeTime(league.lastPlayed)
    : i18n.t("dashboard.notPlayedYet", {
        ns: "ui",
        defaultValue: "Not played yet",
      });
  const phaseLabel = translatePhaseText(league.phaseText);

  return (
    <MobileContentHero
      className="mb-3"
      data-dashboard-save-hero
      data-dashboard-continue-panel
      data-v5-dashboard-save-first-screen
      eyebrow={i18n.t("dashboard.returningUser", {
        ns: "ui",
        defaultValue: "Recent save",
      })}
      title={i18n.t("dashboard.continueLastLeague", {
        ns: "ui",
        defaultValue: "Continue career",
      })}
      explainer={`${league.name} · ${teamName}`}
      action={
        <div data-dashboard-primary-action style={{ minWidth: 180 }}>
          <ContinueButton
            disabled={disabled}
            lid={league.lid}
            onClick={() => setLoadingLID(league.lid)}
            throbbing={throbbing}
          />
        </div>
      }
    >
      <div className="d-flex align-items-center gap-3" data-dashboard-save-identity>
        <TeamLogoInline imgURL={league.imgURL} size={48} />
        <div className="min-w-0">
          <div className="fw-semibold text-truncate" data-dashboard-save-name>
            {league.name}
          </div>
          <div className="text-body-secondary text-truncate" data-dashboard-team-context>
            {teamName}
          </div>
          <div className="small text-body-secondary" data-dashboard-season-state>
            {i18n.t("dashboard.phase", { ns: "ui", defaultValue: "Phase" })}: {phaseLabel}
          </div>
          {careerTenure !== undefined ? (
            <div className="small text-body-secondary" data-dashboard-career-tenure="">
              {i18n.t("dashboard.careerTenure", {
                ns: "ui",
                count: careerTenure,
                defaultValue: "Season {{count}} as GM",
              })}
            </div>
          ) : null}
          <MobileTooltip title={league.lastPlayed?.toLocaleString() ?? lastPlayed}>
            <div className="small text-body-secondary">
              {i18n.t("dashboard.lastPlayed", {
                ns: "ui",
                defaultValue: "Last played",
              })}
              : {lastPlayed}
            </div>
          </MobileTooltip>
        </div>
      </div>
    </MobileContentHero>
  );
};

const NewLeagueLinks = ({
  ownedLeagueCreationTypes,
}: {
  ownedLeagueCreationTypes: View<"dashboard">["ownedLeagueCreationTypes"];
}) => {
  const hasRealLeague = ownedLeagueCreationTypes.includes("real");
  const hasRandomLeague = ownedLeagueCreationTypes.includes("random");
  const canCreateRealLeague = REAL_PLAYERS_INFO !== undefined && !hasRealLeague;
  const canCreateRandomLeague = !hasRandomLeague;
  const primaryHref = canCreateRealLeague
    ? "/new_league/real"
    : canCreateRandomLeague
      ? "/new_league/random"
      : "/new_league";
  const primaryModeLabel = canCreateRealLeague
    ? i18n.t("dashboard.realPlayers", {
        ns: "ui",
        defaultValue: "Real players",
      })
    : i18n.t("dashboard.randomPlayers", {
        ns: "ui",
        defaultValue: "Random players",
      });

  if (REAL_PLAYERS_INFO) {
    if (hasRealLeague && hasRandomLeague) {
      return (
        <div className="alert alert-secondary mb-3" data-dashboard-new-league-secondary-card>
          {i18n.t("dashboard.allLeagueTypesUsed", {
            ns: "ui",
            defaultValue:
              "You already have one league of each type. Delete an existing league before creating another.",
          })}
        </div>
      );
    }
  }

  if (!REAL_PLAYERS_INFO && hasRandomLeague) {
    return null;
  }

  return (
    <MobileContentHero
      className="mb-3"
      data-dashboard-new-league-secondary-card
      eyebrow={i18n.t("dashboard.newChallengeEyebrow", {
        ns: "ui",
        defaultValue: "New challenge",
      })}
      title={i18n.t("dashboard.newChallengeTitle", {
        ns: "ui",
        defaultValue: "Start a new manager run",
      })}
      explainer={i18n.t("dashboard.newChallengeBody", {
        ns: "ui",
        defaultValue:
          "Pick a team, accept the opening challenge, and get to your first roster decision.",
      })}
      action={
        <a className="btn btn-outline-secondary w-100" href={primaryHref}>
          {i18n.t("dashboard.startNewChallenge", {
            ns: "ui",
            defaultValue: "Start new challenge",
          })}
        </a>
      }
    >
      <div className="d-flex flex-wrap gap-2">
        <span className="badge text-bg-light border">{primaryModeLabel}</span>
      </div>
      {canCreateRealLeague && canCreateRandomLeague ? (
        <details className="mt-3" data-native-details-host="dashboard-challenge-modes">
          <summary className="small text-body-secondary">
            {i18n.t("dashboard.moreChallengeModes", {
              ns: "ui",
              defaultValue: "More challenge modes",
            })}
          </summary>
          <a className="btn btn-link btn-sm px-0" href="/new_league/random">
            {i18n.t("dashboard.randomPlayers", {
              ns: "ui",
              defaultValue: "Random players",
            })}
          </a>
        </details>
      ) : null}
    </MobileContentHero>
  );
};

const Dashboard = ({ leagues, ownedLeagueCreationTypes }: View<"dashboard">) => {
  const [loadingLID, setLoadingLID] = useState<number | undefined>();
  const [deletingLID, setDeletingLID] = useState<number | undefined>();
  useTitleBar();

  const disabled = deletingLID !== undefined || loadingLID !== undefined;
  const dashboardLeagueState = getDashboardLeagueState(leagues);
  const { continueLeague, overflowLeagues, recentLeagues } = dashboardLeagueState;
  const libraryLeagues = recentLeagues.filter((league) => league.lid !== continueLeague?.lid);

  return (
    <div data-dashboard-league-count={leagues.length}>
      {leagues.length > 0 ? (
        <>
          <div className="dashboard-entry-card-grid" data-dashboard-secondary-entry>
            {continueLeague ? (
              <MobileSaveHero
                disabled={disabled}
                league={continueLeague}
                setLoadingLID={setLoadingLID}
                throbbing={loadingLID === continueLeague.lid}
              />
            ) : null}

            <NewLeagueLinks ownedLeagueCreationTypes={ownedLeagueCreationTypes} />
          </div>

          {libraryLeagues.length > 0 ? (
            <section className="mb-3" data-dashboard-recent-leagues>
              <h2 className="h5 mb-3">
                {i18n.t("dashboard.otherRecentLeagues", {
                  ns: "ui",
                  defaultValue: "Other recent leagues",
                })}
              </h2>
              <LeagueGrid
                disabled={disabled}
                leagues={libraryLeagues}
                loadingLID={loadingLID}
                setDeletingLID={setDeletingLID}
                setLoadingLID={setLoadingLID}
              />
            </section>
          ) : null}

          {overflowLeagues.length > 0 ? (
            <details
              className="mb-3"
              data-dashboard-all-leagues
              data-dashboard-league-management-more
              data-native-details-host="dashboard-all-leagues"
            >
              <summary className="btn btn-outline-secondary btn-sm">
                {i18n.t("dashboard.allLeagues", {
                  ns: "ui",
                  defaultValue: "All leagues",
                })}{" "}
                <span className="text-body-secondary">({dashboardLeagueState.allLeagueCount})</span>
              </summary>
              <div className="mt-3">
                <LeagueGrid
                  disabled={disabled}
                  leagues={overflowLeagues}
                  loadingLID={loadingLID}
                  setDeletingLID={setDeletingLID}
                  setLoadingLID={setLoadingLID}
                />
              </div>
            </details>
          ) : null}
        </>
      ) : (
        <MobileContentHero
          data-dashboard-new-user-hero
          data-v5-dashboard-new-user-first-screen
          eyebrow={i18n.t("dashboard.newUserEyebrow", {
            ns: "ui",
            defaultValue: "New manager",
          })}
          title={i18n.t("dashboard.noLeaguesTitle", {
            ns: "ui",
            defaultValue: "No leagues yet",
          })}
          explainer={i18n.t("dashboard.noLeaguesBody", {
            ns: "ui",
            defaultValue: "Create a league to start playing.",
          })}
          action={
            <a
              className="btn btn-primary btn-lg px-5"
              href="/new_league"
              data-dashboard-primary-action
            >
              {i18n.t("dashboard.quickStart", {
                ns: "ui",
                defaultValue: "Create your own team",
              })}
            </a>
          }
        >
          <div className="mt-3" data-dashboard-new-user-secondary>
            <p className="small text-body-secondary mb-2" data-dashboard-value-prop>
              {i18n.t("dashboard.valueProp", {
                ns: "ui",
                defaultValue:
                  "Build the roster, make trades, advance the season, and chase a title.",
              })}
            </p>
          </div>
        </MobileContentHero>
      )}
    </div>
  );
};

export default Dashboard;
